// src/pages/AgencyPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check, ExternalLink, RefreshCw, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  'https://ai-workshop-llc-backend.onrender.com';

function RequestAccessForm() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ agencyName: '', email: '', use_case: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/agency/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success('Request received — we\'ll send your API key within 24 hours.');
    } catch {
      toast.error('Could not submit. Email support@aiworkshopllc.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return <p className="text-xs text-[#3b82f6] mt-1">✓ Request sent! Check your inbox within 24 hours.</p>;
  }

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs text-purple-400 hover:underline flex items-center gap-1"
      >
        Don't have a key? Request access {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required value={form.agencyName} onChange={e => setForm(p => ({ ...p, agencyName: e.target.value }))}
              placeholder="Agency / company name"
              className="bg-slate-950/50 border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
            <input required type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="Work email"
              className="bg-slate-950/50 border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
          </div>
          <input value={form.use_case} onChange={e => setForm(p => ({ ...p, use_case: e.target.value }))}
            placeholder="How will you use the API? (optional)"
            className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
          <button type="submit" disabled={submitting}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-full transition-colors">
            {submitting ? 'Sending…' : 'Request API access'}
          </button>
        </form>
      )}
    </div>
  );
}

export default function AgencyPage() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('agency_api_key') || '');
  const [copied, setCopied] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Generate report form state
  const [form, setForm] = useState({
    businessName: '',
    industry: '',
    employees: 10,
    revenue: 'under_1m',
    budget: 'low',
    technicalSkill: 'medium',
    primaryChallenges: '',
    existingTools: '',
  });
  const [generating, setGenerating] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!apiKey) return;
    setLoadingReports(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/agency/reports`, {
        headers: { 'X-API-Key': apiKey },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setReports(data.reports || []);
    } catch (e) {
      toast.error('Could not load reports. Check your API key.');
    } finally {
      setLoadingReports(false);
    }
  }, [apiKey]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const copyKey = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const saveKey = (k) => {
    setApiKey(k);
    localStorage.setItem('agency_api_key', k);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!apiKey) { toast.error('Enter your API key first'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({
          ...form,
          employees: Number(form.employees),
          primaryChallenges: form.primaryChallenges.split(',').map(s => s.trim()).filter(Boolean),
          existingTools: form.existingTools.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      toast.success(`Report started! ID: ${data.reportId}`);
      setForm({ businessName: '', industry: '', employees: 10, revenue: 'under_1m', budget: 'low', technicalSkill: 'medium', primaryChallenges: '', existingTools: '' });
      setTimeout(fetchReports, 2000);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const INDUSTRIES = ['Retail', 'Hospitality', 'Healthcare', 'Technology', 'Manufacturing', 'Real Estate', 'Professional Services', 'Education', 'Finance', 'E-commerce', 'Food & Beverage', 'Legal'];

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-5xl mx-auto px-4 py-12 space-y-10">

        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-1.5 mb-4">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300 font-medium">Agency Partner Portal</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">AI Workshop Agency Dashboard</h1>
          <p className="text-slate-400 max-w-xl mx-auto">Generate AI blueprints for your clients at scale. White-label our engine under your brand.</p>
        </div>

        {/* API Key section */}
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Your API Key</h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={apiKey}
              onChange={e => saveKey(e.target.value)}
              placeholder="ak_xxxxxxxxxxxxxxxxxxxxxxxx"
              className="flex-1 bg-slate-950/50 border border-white/15 rounded-xl px-4 py-2.5 text-slate-200 font-mono text-sm focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={copyKey}
              disabled={!apiKey}
              className="flex items-center gap-2 bg-slate-950/50 hover:bg-white/5 border border-white/10 disabled:opacity-40 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-[#3b82f6]" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <RequestAccessForm />
        </div>

        {/* Generate Report form */}
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-5">Generate Report for a Client</h2>
          <form onSubmit={handleGenerate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Business Name *</label>
              <input required value={form.businessName} onChange={e => setForm(p => ({ ...p, businessName: e.target.value }))}
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Industry *</label>
              <select required value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500">
                <option value="">Select industry…</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Employees</label>
              <input type="number" min={1} value={form.employees} onChange={e => setForm(p => ({ ...p, employees: e.target.value }))}
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Monthly Budget</label>
              <select value={form.budget} onChange={e => setForm(p => ({ ...p, budget: e.target.value }))}
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500">
                <option value="low">Low (&lt;$500/mo)</option>
                <option value="medium">Medium ($500–$2k/mo)</option>
                <option value="high">High (&gt;$2k/mo)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Technical Skill</label>
              <select value={form.technicalSkill} onChange={e => setForm(p => ({ ...p, technicalSkill: e.target.value }))}
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500">
                <option value="low">Low — no IT staff</option>
                <option value="medium">Medium — some tech comfort</option>
                <option value="high">High — dedicated IT</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Primary Challenges (comma-separated)</label>
              <input value={form.primaryChallenges} onChange={e => setForm(p => ({ ...p, primaryChallenges: e.target.value }))}
                placeholder="e.g. lead gen, customer service, scheduling"
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Existing Tools (comma-separated)</label>
              <input value={form.existingTools} onChange={e => setForm(p => ({ ...p, existingTools: e.target.value }))}
                placeholder="e.g. QuickBooks, Shopify, HubSpot"
                className="w-full bg-[#0f172a] border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={generating}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2">
                {generating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating…</> : <><Zap className="w-4 h-4" /> Generate Blueprint</>}
              </button>
            </div>
          </form>
        </div>

        {/* Reports table */}
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white">Client Reports</h2>
            <button onClick={fetchReports} disabled={loadingReports}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
              <RefreshCw className={`w-4 h-4 ${loadingReports ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {reports.length === 0 ? (
            <p className="text-slate-500 text-center py-8 text-sm">
              {apiKey ? 'No reports yet — generate your first one above.' : 'Enter your API key to view reports.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/8 text-left">
                    <th className="pb-3 text-slate-400 font-medium">Business</th>
                    <th className="pb-3 text-slate-400 font-medium">Industry</th>
                    <th className="pb-3 text-slate-400 font-medium">Date</th>
                    <th className="pb-3 text-slate-400 font-medium">Status</th>
                    <th className="pb-3 text-slate-400 font-medium">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.reportId} className="border-b border-white/8 hover:bg-white/5">
                      <td className="py-3 text-white font-medium">{r.businessName || '—'}</td>
                      <td className="py-3 text-slate-300">{r.industry || '—'}</td>
                      <td className="py-3 text-slate-400">
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                          r.status === 'complete'
                            ? 'bg-blue-500/20 text-[#3b82f6]'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {r.status === 'complete' ? 'Complete' : 'Generating…'}
                        </span>
                      </td>
                      <td className="py-3">
                        <a href={r.reportUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors">
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
